import React, { useMemo, useCallback } from 'react';
import { Formik, Form } from 'formik';

import { FormikInput, ModalSubmit } from 'components/common';
import { Modal } from 'components/bootstrap';
import type { EntityShare, EntitySharePayload } from 'actions/permissions/EntityShareActions';
import type { CollectionListItem, CollectionDetail, Collection } from 'collections/types';
import EntityCreateShareFormGroup from 'components/permissions/EntityCreateShareFormGroup';

type FormValues = Partial<
  Pick<CollectionListItem, 'id' | 'title' | 'description' | 'shared_with_teams' | 'shared_with_users'>
> & {
  share_request?: EntitySharePayload;
};

const prepareInitialCollection = (initialCollection: Collection) => ({
  title: initialCollection?.title ?? '',
  description: initialCollection?.description ?? '',
});

const validate = (values: FormValues) => {
  let errors = {};

  if (!values.title) {
    errors = { ...errors, title: 'Title is required' };
  }

  if (!values.description) {
    errors = { ...errors, description: 'Description is required' };
  }

  return errors;
};

type Props = {
  title: string;
  submitButtonText: string;
  submitLoadingText: string;
  onClose: () => void;
  onSubmit: (values: Partial<CollectionDetail> & EntityShare) => Promise<Object>;
  create?: boolean;
  initialCollection?: Collection;
};

const CollectionModal = ({
  title: modalTitle,
  submitButtonText,
  submitLoadingText,
  onClose,
  onSubmit,
  create = false,
  initialCollection = null,
}: Props) => {
  const initialValues = useMemo(() => prepareInitialCollection(initialCollection), [initialCollection]);

  const onSubmitChained = useCallback(
    (values: FormValues) => onSubmit(values).then(() => onClose()),
    [onClose, onSubmit],
  );

  return (
    <Modal onHide={onClose} show>
      <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmitChained} validate={validate}>
        {({ isSubmitting, isValidating, setFieldValue }) => (
          <Form>
            <Modal.Header>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormikInput label="Title" name="title" id="title" help="A descriptive name of the new collection" />
              <FormikInput
                label="Description"
                name="description"
                id="description"
                help="What kind of entities will be in this collection?"
              />
              {create && (
                <EntityCreateShareFormGroup
                  description="Search for a User or Team to add as collaborator on this Collection."
                  entityType="collection"
                  onSetEntityShare={(payload) => setFieldValue('share_request', payload)}
                />
              )}
            </Modal.Body>
            <Modal.Footer>
              <ModalSubmit
                submitButtonText={submitButtonText}
                submitLoadingText={submitLoadingText}
                onCancel={onClose}
                disabledSubmit={isValidating}
                isSubmitting={isSubmitting}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default CollectionModal;
